import {
  Attributes,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Heading, Show, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {array, object, string} from 'yup';

import {type UseFormReturn} from 'react-hook-form';

import {isNil, omit} from 'ramda';

import {useGetTenantV2Query, usePutTenantV2Mutation} from '@dms/api/base';
import {type AddrDetailApiResponse} from '@dms/api/core';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {
  AddressSearch,
  handleApiError,
  useCountry,
  useCurrencies,
  usePhoneNumbers,
} from '@dms/shared';

import {Nullish, yupString} from 'shared/web';

import {ContactPersonList} from './components/ContactPersonList';
import {COMPANY_TYPE} from './constants/COMPANY_TYPE';
import {SELF_EMPLOYED_TYPE} from './constants/SELF_EMPLOYED_TYPE';
import {TENANT_TYPE_OPTIONS} from './constants/TENANT_TYPE_OPTIONS';
import {type FormValues} from './types';
import {type TenantType} from './types/TenantType';
import {normalizeContactInformation} from './utils/normalizeContactInformation';
import {normalizeSelfEmployedInformation} from './utils/normalizeSelfEmployedInformation';

const getTenantType = (type: FormValues['type']): TenantType =>
  type.includes(COMPANY_TYPE) ? COMPANY_TYPE : SELF_EMPLOYED_TYPE;

export function TenantProfile() {
  const {countriesOptions} = usePhoneNumbers();
  const {getCountryName} = useCountry();
  const {getCurrencyName, getCurrencySymbol} = useCurrencies();

  const {data: tenant, isLoading, isError} = useGetTenantV2Query();
  const [updateTenant] = usePutTenantV2Mutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    reset,
    _options
  ) => {
    const {type, contactInformation, publicContacts, contactPersons} = values;
    const tenantType = getTenantType(type);

    await updateTenant({
      type: tenantType,
      contactInformation: normalizeContactInformation(tenantType, contactInformation),
      publicContacts,
      contactPersons,
    })
      .unwrap()
      .then(() => {
        showNotification.success();
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);
  };

  const setAddressFormSearch =
    (formApi: UseFormReturn<FormValues>) => (address: AddrDetailApiResponse) => {
      if (isNil(tenant)) {
        showNotification.error();
        return;
      }

      formApi.setValue(
        'contactInformation.address',
        {
          city: address?.city ?? '',
          street: address?.street ?? '',
          zip: address?.zipCode ?? '',
          state: address?.state ?? '',
          prefix: address?.prefix ?? '',
          descriptiveNumber: address?.descriptiveNumber ?? null,
          orientationNumber: address?.orientationNumber ?? null,
          addressComplement: null,
          district: null,
          country: tenant.contactInformation.address.country,
          coordinates: null,
          postal: null,
        },
        {shouldValidate: true}
      );
    };

  const defaultValues = {
    ...(tenant ? omit(['billingInformation'], tenant) : {}),
    type: [tenant?.type ?? SELF_EMPLOYED_TYPE],
  };

  const handleOnTypeChange = (formApi: UseFormReturn<FormValues>) => (val: string[] | Nullish) => {
    if (isNil(val)) {
      return null;
    }

    const tenantType = getTenantType(val);

    return match<TenantType, null>(tenantType)
      .with(SELF_EMPLOYED_TYPE, () => {
        formApi.setValue(
          'contactInformation.selfEmployedInformation',
          normalizeSelfEmployedInformation(
            formApi.getValues('contactInformation.selfEmployedInformation')
          ),
          {shouldValidate: true}
        );
        return null;
      })
      .with(COMPANY_TYPE, () => {
        formApi.setValue('contactInformation.selfEmployedInformation', null, {
          shouldValidate: true,
        });
        return null;
      })
      .exhaustive();
  };

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.settings.labels.tenantProfile')}}
      isLoading={isLoading}
      isError={isError}
      data-testid="tenant-profile-template"
    >
      <Form<FormValues>
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        schema={schema}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi) => {
          const isTenantSelfEmployed = formApi.watch('type')?.includes(SELF_EMPLOYED_TYPE) ?? true;
          const handleDiscardChanges = () => formApi.reset(defaultValues);

          return (
            <VStack>
              <SettingsSection>
                <Grid columns={12}>
                  <GridItem span={12}>
                    <FormField
                      name="type"
                      control={control}
                      type="chips"
                      options={chipsOptions}
                      onChange={handleOnTypeChange(formApi)}
                    />
                  </GridItem>

                  <Show when={isTenantSelfEmployed}>
                    <GridItem span={2}>
                      <FormField
                        name="contactInformation.selfEmployedInformation.titleBefore"
                        label={i18n.t('entity.person.labels.titleBefore')}
                        control={control}
                        type="text"
                      />
                    </GridItem>
                    <GridItem span={4}>
                      <FormField
                        name="contactInformation.selfEmployedInformation.firstName"
                        label={i18n.t('entity.person.labels.firstName')}
                        isRequired={isTenantSelfEmployed}
                        control={control}
                        type="text"
                      />
                    </GridItem>

                    <GridItem span={4}>
                      <FormField
                        name="contactInformation.selfEmployedInformation.lastName"
                        label={i18n.t('entity.person.labels.lastName')}
                        control={control}
                        isRequired={isTenantSelfEmployed}
                        type="text"
                      />
                    </GridItem>
                    <GridItem span={2}>
                      <FormField
                        name="contactInformation.selfEmployedInformation.titleAfter"
                        label={i18n.t('entity.person.labels.titleAfter')}
                        control={control}
                        type="text"
                      />
                    </GridItem>

                    <GridItem span={6}>
                      <FormField
                        name="contactInformation.selfEmployedInformation.phoneNumber"
                        label={i18n.t('entity.person.labels.phoneNumber')}
                        control={control}
                        type="phone"
                        countries={countriesOptions}
                      />
                    </GridItem>
                    <GridItem span={6}>
                      <FormField
                        name="contactInformation.selfEmployedInformation.emailAddress"
                        label={i18n.t('entity.email.labels.email')}
                        control={control}
                        type="email"
                      />
                    </GridItem>
                  </Show>
                  <GridItem span={6}>
                    <FormField
                      name="contactInformation.registrationNumber"
                      label={i18n.t('entity.invoice.labels.registrationNumber')}
                      control={control}
                      type="text"
                    />
                  </GridItem>
                  <GridItem span={6}>
                    <FormField
                      name="contactInformation.vatNumber"
                      label={i18n.t('entity.invoice.labels.vatNumber')}
                      control={control}
                      type="text"
                    />
                  </GridItem>

                  <GridItem span={6}>
                    <FormField
                      name="contactInformation.companyName"
                      label={i18n.t('entity.customer.labels.companyName')}
                      control={control}
                      type="text"
                    />
                  </GridItem>
                  <GridItem span={6}>
                    <FormField
                      name="contactInformation.fileNumber"
                      label={i18n.t('entity.customer.labels.fileNumber')}
                      control={control}
                      type="text"
                    />
                  </GridItem>
                </Grid>
              </SettingsSection>
              <Separator spacing={5} />

              <SettingsSection>
                <Heading size={4}>{i18n.t('entity.address.labels.address')}</Heading>

                <Space vertical={5} />

                <Grid columns={12}>
                  <GridItem span={12}>
                    <AddressSearch onSelect={setAddressFormSearch(formApi)} />
                  </GridItem>

                  <GridItem span={6}>
                    <FormField
                      name="contactInformation.address.street"
                      label={i18n.t('entity.address.labels.street')}
                      control={control}
                      type="text"
                      isRequired
                    />
                  </GridItem>
                  <GridItem span={3}>
                    <FormField
                      name="contactInformation.address.descriptiveNumber"
                      label={i18n.t('entity.address.labels.descriptiveNumber')}
                      control={control}
                      type="text"
                    />
                  </GridItem>
                  <GridItem span={3}>
                    <FormField
                      name="contactInformation.address.orientationNumber"
                      label={i18n.t('entity.address.labels.orientationNumber')}
                      control={control}
                      type="text"
                    />
                  </GridItem>
                  <GridItem span={6}>
                    <FormField
                      name="contactInformation.address.city"
                      label={i18n.t('entity.address.labels.city')}
                      control={control}
                      type="text"
                      isRequired
                    />
                  </GridItem>

                  <GridItem span={3}>
                    <FormField
                      name="contactInformation.address.district"
                      label={i18n.t('entity.address.labels.district')}
                      control={control}
                      type="text"
                    />
                  </GridItem>
                  <GridItem span={3}>
                    <FormField
                      name="contactInformation.address.zip"
                      label={i18n.t('entity.address.labels.zipCode')}
                      control={control}
                      type="text"
                      isRequired
                    />
                  </GridItem>
                </Grid>
              </SettingsSection>

              <Separator spacing={5} />
              <SettingsSection>
                <VStack spacing={5}>
                  <Heading size={4}>{i18n.t('entity.customer.labels.publicContacts')}</Heading>
                  <Text size="small" color="secondary">
                    {i18n.t('entity.customer.labels.publicContactsHelperText')}
                  </Text>
                  <Grid columns={2}>
                    <FormField
                      name="publicContacts.phoneNumber"
                      label={i18n.t('entity.person.labels.phoneNumber')}
                      control={control}
                      countries={countriesOptions}
                      type="phone"
                    />
                    <FormField
                      name="publicContacts.emailAddress"
                      label={i18n.t('general.labels.emailAddress')}
                      control={control}
                      type="email"
                    />
                    <GridItem span={2}>
                      <FormField
                        name="publicContacts.website"
                        label={i18n.t('general.labels.website')}
                        control={control}
                        type="text"
                      />
                    </GridItem>
                  </Grid>
                </VStack>
              </SettingsSection>

              <Separator spacing={5} />

              <ContactPersonList control={control} formApi={formApi} />

              <Separator spacing={5} />
              <SettingsSection>
                <VStack spacing={5}>
                  <Heading size={4}>{i18n.t('page.settings.labels.systemInformation')}</Heading>
                  <Attributes
                    size="quarter"
                    rows={[
                      {
                        label: i18n.t('page.settings.labels.tenantLocation'),
                        value: getCountryName(tenant?.country),
                      },
                      {
                        label: i18n.t('page.settings.labels.primaryCurrency'),
                        value: getCurrencyName(tenant?.currency),
                      },
                      {
                        label: i18n.t('general.labels.currencySymbol'),
                        value: getCurrencySymbol(tenant?.currency),
                      },
                      {label: i18n.t('page.settings.labels.tenantId'), value: tenant?.id},
                    ]}
                  />
                </VStack>
              </SettingsSection>

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: handleDiscardChanges,
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.save'),
                  },
                ]}
              />
            </VStack>
          );
        }}
      </Form>
    </SettingsTemplate>
  );
}

const schema = object({
  contactPersons: array().default([]),
  type: array()
    .of(string().oneOf([...TENANT_TYPE_OPTIONS]))
    .min(1)
    .required(),
  contactInformation: object({
    selfEmployedInformation: object({
      titleBefore: yupString,
      firstName: yupString.required(),
      lastName: yupString.required(),
      titleAfter: yupString,
      emailAddress: yupString,
      phoneNumber: object().nullable(),
    })
      .default(null)
      .nullable(),
    registrationNumber: yupString.required(),
    vatNumber: yupString,
    companyName: yupString.required(),
    fileNumber: yupString,
    address: object({
      street: yupString.required(),
      descriptiveNumber: yupString,
      orientationNumber: yupString,
      city: yupString.required(),
      district: yupString,
      zip: yupString.required(),
      coordinates: object({
        latitude: yupString,
        longitude: yupString,
      })
        .optional()
        .nullable(),
      postal: yupString,
    }),
  }),
});

const chipsOptions = [
  {
    value: SELF_EMPLOYED_TYPE,
    label: i18n.t('entity.customer.labels.isSelfEmployed'),
  },
  {
    value: COMPANY_TYPE,
    label: i18n.t('general.labels.company'),
  },
];
